import React, { ReactNode, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Link as MuiLink,
  Paper,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import DeveloperModeOutlinedIcon from '@mui/icons-material/DeveloperModeOutlined';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import DevicesOutlinedIcon from '@mui/icons-material/DevicesOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import './OnboardingPage.css';

type IconComponent = React.ElementType;

const navItems = [
  { id: 'environments', label: 'Environments' },
  { id: 'data-collection-tools', label: 'Data Collection Tools' },
  { id: 'data-dissemination-tools', label: 'Data Dissemination Tools' }
];

const environmentCards: Array<{ title: string; description: string; Icon: IconComponent }> = [
  {
    title: 'Development',
    description:
      'Used exclusively for tool and protocol development. No real participant data! The sandbox allows the team to iterate quickly, but it can be unstable as features are actively tested and deployed.',
    Icon: DeveloperModeOutlinedIcon
  },
  {
    title: 'Staging',
    description:
      'Used exclusively for new user onboarding and quality checks. No real participant data! Provides a stable environment with persistent data so new users can demonstrate competency.',
    Icon: ThumbUpAltOutlinedIcon
  },
  {
    title: 'Production',
    description:
      'Used exclusively for real participant data collection. Real participant data! Reserved for trained B2Ai Voice DGP members actively collecting study data.',
    Icon: PublicOutlinedIcon
  }
];

const dataCollectionTools: Array<{
  title: string;
  logo?: string;
  about: string;
  prerequisites: ReactNode;
  howToAccess: ReactNode;
  resources: Array<{label: string; url: string; Icon?: IconComponent }>;
}> = [
  {
    title: 'Register Web App',
    logo: '/assets/images/logo.svg',
    about: "Our web tool for any interested person in the US to register to participate in B2Ai Voice.",
    prerequisites: (<>
      Available publicly to anyone in the US. <br/>
      <Typography variant="body2" sx={{ textDecoration: 'underline', mr: 0.5 }} display="inline">
        Note:
      </Typography>
      This tool is not offered in Canada as Canadian sites have opted out of storing the email of
      the participant in B2Ai servers, a required PII to send a one-time-password OTP to
      authenticate to the data collection app.
    </>),
    howToAccess: (<>Go to <MuiLink href="https://app.b2ai-voice.org/register" target="_blank" rel="noopener noreferrer" color="inherit" underline="always">https://app.b2ai-voice.org/register</MuiLink></>),
    resources: [
      {label: 'Participant Registration Customization', url: 'https://usf.box.com/s/fi8dsaakq81qt0kycgevilirkapwp4ek', Icon: DescriptionOutlinedIcon },
      {label: 'Tutorial', url: 'https://usf.box.com/s/z812gror7dzpe0pz04rd2atl4yxfen8u', Icon: DescriptionOutlinedIcon }
    ],
  }, {
    title: 'Data Collection Web App',
    logo: '/assets/images/logo.svg',
    about: 'Our web tool for any enrolled participant in the US to collect data remotely.',
    prerequisites: (<>
      Available to all enrolled B2Ai Voice Participants in the US. <br/>
      <Typography variant="body2" sx={{ textDecoration: 'underline', mr: 0.5 }} display="inline">
        Note:
      </Typography>
      This tool is not offered in Canada as Canadian sites have opted out of storing the email of the participant in B2Ai servers, a required PII to send a one-time-password OTP to authenticate to the data collection app.
    </>),
    howToAccess: (<>Participants simply navigate to <MuiLink href="/" target="_blank" rel="noopener noreferrer" color="inherit" underline="always">{window.location.origin}</MuiLink> and sign in with the email they provided during registration.
     <br/>
      <Typography variant="body2" sx={{ textDecoration: 'underline', mr: 0.5 }} display="inline">
        Note:
      </Typography>
      Data collection is enabled only for consented participants.</>),
    resources: [
      {label: 'Go to Data Collection App', url: '/' },
      {label: 'Tutorial', url: 'https://usf.box.com/s/z812gror7dzpe0pz04rd2atl4yxfen8u', Icon: DescriptionOutlinedIcon }
    ]
  }, {
    title: 'App',
    logo: '/assets/images/logo.svg',
    about: 'Our app for iOS, iPadOS, MacOS, and VisionOS to register and perform data collection on a B2Ai Voice participant.',
    prerequisites: (<>To access B2Ai Voice - App, you will need to:
    <ul>
      <li>Be a B2Ai Voice member, actively involved with enrollment and/or data collection in one of our B2Ai Voice Clinic Sites. This further requires:</li>
      <ul>
        <li>A signed DUA between your institution and USF</li>
        <li>Be part of your site’s local IRB/REB for the B2Ai Voice Study.</li>
      </ul>
    </ul>
    </>),
    howToAccess: (<>Fill out the Monday form here:
      <MuiLink href="https://wkf.ms/3Q2FHSZ" target="_blank" rel="noopener noreferrer" color="inherit" underline="always" sx={{ ml: 0.5 }}>https://wkf.ms/3Q2FHSZ</MuiLink><br/>
      <Typography variant="body2" sx={{ textDecoration: 'underline', mr: 0.5 }} display="inline">
        Note:
      </Typography>
      Our B2Ai Voice - App is currently available to approved devices and delivered through Apple’s TestFlight app.
    </>),
    resources: [
      {label: 'Hardware Prerequisites', url: 'https://usf.box.com/s/50um438gjwx3g81pbina765kfvuafv98', Icon: DevicesOutlinedIcon },
      {label: 'How to Setup Guided Access', url: 'https://support.apple.com/en-lamr/111795' },
      {label: 'Testing Apps & Giving Feedback with TestFlight', url: 'https://testflight.apple.com/' }
    ]
  }, {
    title: 'B2Ai Voice - REDCap',
    logo: '/assets/images/redcap-logo-large.png',
    about: 'B2Ai Voice - REDCap is our internal database tool for storing participant collected information during registration and data collection.',
    prerequisites: (<>To access B2Ai Voice REDCap, you will need to:
      <ul>
        <li>Be a B2Ai Voice member, actively involved with enrollment and/or data collection in one of our B2Ai Voice Clinic Sites. This further requires:
        <ul>
          <li>A signed DUA between your institution and USF</li>
          <li>Be part of your site’s local IRB/REB for the B2Ai Voice Study.</li>
        </ul>
      </li>
    </ul>
    </>),
    howToAccess: (<>Fill out the Monday form here:
      <MuiLink href="https://wkf.ms/3Q2FHSZ" target="_blank" rel="noopener noreferrer" color="inherit" underline="always" sx={{ ml: 0.5 }}>https://wkf.ms/3Q2FHSZ</MuiLink>
    </>),
    resources: [
      {label: 'REDCap Website', url: 'https://redcap.ctsc.weill.cornell.edu/' },
      {label: 'REDCap Training Videos – 5+ hours of REDCap Training Videos', url: 'https://redcap.ctsc.weill.cornell.edu/redcap_protocols/index.php?action=training', Icon: PlayCircleOutlineIcon }
    ]
  }, {
    title: 'Dashboard Web App',
    logo: '/assets/images/logo.svg',
    about: 'Our web tool to visualize and access B2Ai Voice enrollment, data collection and data dissemination metrics and numbers updated on an hourly basis.',
    prerequisites: (<>Available to all B2Ai Voice members that are actively involved with enrollment and/or data collection in one of our B2Ai Voice Clinic sites.</>),
    howToAccess: (<>Fill out the Monday form here:
      <MuiLink href="https://wkf.ms/3Q2FHSZ" target="_blank" rel="noopener noreferrer" color="inherit" underline="always" sx={{ ml: 0.5 }}>https://wkf.ms/3Q2FHSZ</MuiLink>
    </>),
    resources: [
      {label: 'Go to Dashboard', url: 'https://dashboard.b2ai-voice.org/' },
      {label: 'Tutorial', url: 'https://usf.box.com/s/z812gror7dzpe0pz04rd2atl4yxfen8u', Icon: DescriptionOutlinedIcon }
    ]
  }
];

const dataDisseminationTools: Array<{
  title: string;
  logo?: string;
  about: string;
  prerequisites: ReactNode;
  howToAccess: ReactNode;
  resources: Array<{label: string; url: string; Icon?: IconComponent }>;
}> = [
  {
    title: 'B2Ai Voice - Wasabi',
    logo: '/assets/images/wasabi.png',
    about: 'B2Ai Voice Wasabi is our cloud storage tool for our accessing our B2Ai Voice internal data releases.',
    prerequisites: (<>To access B2Ai Voice Wasabi, you will need to:
      <ul>
        <li>Be a B2Ai Voice Member.</li>
        <li>Have a signed DUA between your institution and USF - This should specify access to internal data releases. If unsure, consult with your B2Ai Voice site-PI.</li>
      </ul>
    </>),
    howToAccess: (<>Provided you meet the prerequisites, contact your B2Ai Voice site-PI to request credentials on your behalf.</>),
    resources: [
      {label: 'PDF Guide', url: 'https://usf.box.com/s/174u3wf4f098t9ibxyvrsz2ie8nor2ad', Icon: LibraryBooksOutlinedIcon },
      {label: 'ESS – Recording Link', url: 'https://usf.box.com/s/br4puminxb3lo101srf5kq724y1qjhey', Icon: PlayCircleOutlineIcon },
      {label: 'ESS Slides Link', url: 'https://usf.box.com/s/hckoynqjx59aru20vuga0vwt5nl5dsgt', Icon: SchoolOutlinedIcon }
    ]
  }
];

export const OnboardingPage = () => {
  const theme = useTheme();

  const handleScroll = useCallback((targetId: string) => {
    const target = document.getElementById(targetId);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  return (
    <Box>
      <Paper
        sx={{
          position: 'relative',
          color: theme.palette.common.white,
          backgroundImage: `linear-gradient(${theme.palette.primary.main}, rgba(30,17,105,0.8)), url('/assets/images/b2ai-onboarding.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          py: { xs: 10, md: 14 },
          textAlign: 'center',
          borderRadius: '1rem 1rem 0 0'
        }}
      >
        <Container maxWidth="lg">
          <Box
            component="img"
            src="/assets/images/logo.svg"
            alt="B2Ai logo"
            sx={{ width: { xs: 120, md: 160 }, mb: 3, filter: 'invert(1)' }}
          />
          <Typography variant="h2" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
            Onboarding
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: 720, mx: 'auto' }}>
            Explore the tools, environments, and resources that power B2Ai Voice data collection and dissemination
            across our distributed research teams.
          </Typography>
        </Container>


      </Paper>

      <>
        <Paper
          elevation={3}
          sx={{ p: { xs: 2, md: 3 }, borderRadius: '0 0 1rem 1rem', backgroundColor: theme.palette.background.paper }}
        >
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Jump to a section
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            flexWrap="wrap"
          >
            {navItems.map((item) => (
              <Button
                key={item.id}
                variant="outlined"
                color="primary"
                onClick={() => handleScroll(item.id)}
                className="onboardingpage-nav-button"
              >
                {item.label}
              </Button>
            ))}
          </Stack>
        </Paper>
      </>

      <Container maxWidth="lg" sx={{ mt: { xs: 6, md: 10 } }}>
        <Box component="section" id="environments" sx={{ mb: { xs: 8, md: 10 } }}>
          <Typography variant="h4" sx={{ fontWeight: 600, textAlign: 'center', mb: 4 }}>
            Environments
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Following software engineering best practices, every tool is provided across three environments. Each
            environment serves a specific purpose:
          </Typography>
          <Typography component="div" variant="body1" sx={{ mb: 4, pl: 2 }}>
            <ul>
              <li>Development</li>
              <li>Staging</li>
              <li>Production</li>
            </ul>
          </Typography>
          <Grid container spacing={3}>
            {environmentCards.map(({ title, description, Icon }) => (
              <Grid item xs={12} md={4} key={title}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ color: theme.palette.primary.main, mb: 2, display: 'flex', justifyContent: 'center' }}>
                      <Icon fontSize="large" />
                    </Box>
                    <Typography variant="h6" align="center" gutterBottom>
                      {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      <Container maxWidth="lg" sx={{ mt: { xs: 6, md: 10 } }}>
        <Box component="section" id="data-collection-tools" sx={{ mb: { xs: 8, md: 10 } }}>
          <Typography variant="h4" sx={{ fontWeight: 600, textAlign: 'center', mb: 6 }}>
            Data Collection Tools
          </Typography>
          <Grid container spacing={4} alignItems={'center'}>
            {dataCollectionTools.map(({
              title,
              logo,
              about,
              prerequisites,
              howToAccess,
              resources
            }, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card className="onboardingpage-card" sx={{ backgroundColor: theme.palette.primary.light }}>
                  <CardContent >
                    <Stack spacing={1} direction="column" alignItems="center">
                      {logo && <Box component="img" src={logo} alt={`${title} logo`} style={{ height: 40 }} />}
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {title}
                      </Typography>
                    </Stack>

                    <Stack spacing={2} sx={{ mt: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        About
                      </Typography>
                      <Typography variant="body2" >
                        {about}
                      </Typography>
                    </Stack>

                    <Stack spacing={2} sx={{ mt: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Prerequisites
                      </Typography>
                      <Typography variant="body2" >
                        {prerequisites}
                      </Typography>
                    </Stack>

                    <Stack spacing={2} sx={{ mt: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        How to Access
                      </Typography>
                      <Typography variant="body2" >
                        {howToAccess}
                      </Typography>
                    </Stack>

                    {resources.length > 0 && (
                      <>
                      <Stack spacing={2} sx={{ mt: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Resources
                        </Typography>
                      </Stack>

                      <Grid container sx={{ flexWrap: 'wrap', gap: 1 }}>
                        {resources.map(({ label, url, Icon }) => (
                          <Button
                            className='onboardingpage-card-action-button'
                            key={label}
                            variant="contained"
                            color="primary"
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={Icon ? <Icon /> : null}
                            endIcon={<OpenInNewIcon />}
                            sx={{ textTransform: 'none' }}
                          >
                            {label}
                          </Button>
                        ))}
                      </Grid>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          </Box>
      </Container>



      <Container maxWidth="lg" sx={{ mt: { xs: 6, md: 10 } }}>
        <Box component="section" id="data-dissemination-tools" sx={{ mb: { xs: 8, md: 10 } }}>
          <Typography variant="h4" sx={{ fontWeight: 600, textAlign: 'center', mb: 6 }}>
            Data Dissemination Tools
          </Typography>
          <Grid container spacing={4} >
            {dataDisseminationTools.map(({
              title,
              logo,
              about,
              prerequisites,
              howToAccess,
              resources
            }, index) => (
              <Grid item xs={12} key={index}>
                <Card className="onboardingpage-card" sx={{ backgroundColor: theme.palette.primary.light }}>
                  <CardContent >
                    <Stack spacing={1} direction="column" alignItems="center">
                      {logo && <Box component="img" src={logo} alt={`${title} logo`} style={{ height: 40 }} />}
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {title}
                      </Typography>
                    </Stack>

                    <Stack spacing={2} sx={{ mt: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        About
                      </Typography>
                      <Typography variant="body2" >
                        {about}
                      </Typography>
                    </Stack>

                    <Stack spacing={2} sx={{ mt: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Prerequisites
                      </Typography>
                      <Typography variant="body2" >
                        {prerequisites}
                      </Typography>
                    </Stack>

                    <Stack spacing={2} sx={{ mt: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        How to Access
                      </Typography>
                      <Typography variant="body2" >
                        {howToAccess}
                      </Typography>
                    </Stack>

                    {resources.length > 0 && (
                      <>
                      <Stack spacing={2} sx={{ mt: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Resources
                        </Typography>
                      </Stack>

                      <Grid container sx={{ flexWrap: 'wrap', gap: 1 }}>
                        {resources.map(({ label, url, Icon }) => (
                          <Button
                            className='onboardingpage-card-action-button'
                            key={label}
                            variant="contained"
                            color="primary"
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={Icon ? <Icon /> : null}
                            endIcon={<OpenInNewIcon />}
                            sx={{ textTransform: 'none' }}
                          >
                            {label}
                          </Button>
                        ))}
                      </Grid>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          </Box>
      </Container>
    </Box>
  );
};
